// Explore Service
//
// Real profile photos for the Explore grid, from /feed/explore. Replaces the
// picsum.photos placeholders the tab used to generate for eight invented users.
//
// Explore intentionally casts a wider net than the matching deck: the backend
// keeps only the gender-preference gate, dropping the age, distance and persona
// narrowing, and boosts far-away, under-exposed and new accounts (see
// get_explore_feed, migration 043). This is discovery, not matchmaking.

import { config } from '../../config/env';
import { authInterceptor } from '../Authentication/authInterceptor';
import { logger } from '../../utils/logger';

const API_BASE = `${config.api.baseUrl}/feed`;

export interface ExploreImage {
  id: string;
  userId: string;
  username: string;
  avatar: string;
  imageUrl: string;
  timestamp: Date;
  location: string | null;
  distanceMiles: number | null;
  isNewMember: boolean;
  photoCount: number;
}

interface RawTile {
  id: string;
  userId: string;
  username: string;
  avatar: string;
  imageUrl: string;
  timestamp: string;
  location: string | null;
  distanceMiles: number | null;
  isNewMember: boolean;
  alreadySwiped: boolean;
  photoCount: number;
}

function headers(): HeadersInit {
  return { Authorization: `Bearer ${authInterceptor.getAccessToken() || ''}` };
}

/**
 * One 401/403 recovery attempt, for the same reason momentsService does it: the
 * stored access token can go stale while supabase-js still holds a valid
 * session, and a direct `fetch` never passes through authInterceptor's response
 * hook that would otherwise repair it.
 */
async function request(path: string): Promise<any> {
  const send = () => fetch(`${API_BASE}${path}`, { headers: headers() });

  let response = await send();

  if (response.status === 401 || response.status === 403) {
    logger.log('[EXPLORE] Token rejected, refreshing session and retrying');
    try {
      await authInterceptor.performRefresh();
      response = await send();
    } catch (error) {
      logger.error('[EXPLORE] Token refresh failed:', error);
    }
  }

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      detail = body?.message || body?.error || detail;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(
      response.status === 401 || response.status === 403
        ? 'Your session expired. Please sign in again.'
        : `${detail || 'Request failed'} (HTTP ${response.status})`,
    );
  }

  const body = await response.json();
  return body.data;
}

/**
 * A stable seed for one scroll session. The backend's ordering is deterministic
 * per seed, which is what lets offset paging work — generating a new seed per
 * page would reshuffle the grid and make tiles repeat or disappear mid-scroll.
 */
export function newExploreSeed(): string {
  return Math.random().toString(36).slice(2, 10);
}

class ExploreService {
  async getExploreFeed(limit = 40, offset = 0, seed?: string): Promise<ExploreImage[]> {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (seed) params.set('seed', seed);

    const data = await request(`/explore?${params}`);

    return ((data ?? []) as RawTile[]).map((tile) => ({
      id: tile.id,
      userId: tile.userId,
      username: tile.username,
      avatar: tile.avatar,
      imageUrl: tile.imageUrl,
      timestamp: new Date(tile.timestamp),
      location: tile.location,
      distanceMiles: tile.distanceMiles,
      isNewMember: tile.isNewMember,
      photoCount: tile.photoCount,
    }));
  }
}

export const exploreService = new ExploreService();
